import {
  api,
  PagedResultOfEorSpeakingMonitoringGroup,
  EorMonitoringGroupDetails,
  EorMonitoringNewStageDetails,
  EorMonitoringDetails,
  EorMonitoringReason,
  EorMonitoringType,
  EorMonitoringResult,
  EorMonitoringStage,
  EorSpeakingSuspension,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';

const eorSpeakingMonitoringApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    eorSpeakingMonitoringList: builder.query<
      PagedResultOfEorSpeakingMonitoringGroup,
      { examinerId: number; pageIndex: number; pageSize: number; showDeleted?: boolean }
    >({
      query: ({ examinerId, pageIndex, pageSize, showDeleted }) => {
        return api.eorSpeakingMonitoring.getExaminerEorSpeakingMonitorings({
          examinerId,
          page: pageIndex + 1,
          count: pageSize,
          'filter.showDeleted': showDeleted,
        });
      },
      providesTags: ['EorSpeakingMonitoring'],
    }),
    eorSpeakingMonitoringDetails: builder.query<
      EorMonitoringGroupDetails,
      { monitoringGroupId: number }
    >({
      query: ({ monitoringGroupId }) => {
        return api.eorSpeakingMonitoring.get({ id: monitoringGroupId });
      },
      providesTags: (_result, _error, { monitoringGroupId }) => [
        { type: 'EorSpeakingMonitoring', id: monitoringGroupId },
      ],
    }),
    eorSpeakingMonitoringReasons: builder.query<EorMonitoringReason[], void>({
      query: () => api.eorSpeakingMonitoring.getEorSpeakingMonitoringReasons(),
    }),
    eorSpeakingMonitoringTypes: builder.query<EorMonitoringType[], void>({
      query: () => api.eorSpeakingMonitoring.getEorSpeakingMonitoringTypes(),
    }),
    eorSpeakingMonitoringResults: builder.query<EorMonitoringResult[], void>({
      query: () => api.eorSpeakingMonitoring.getEorSpeakingMonitoringResults(),
    }),
    eorSpeakingMonitoringStages: builder.query<EorMonitoringStage[], void>({
      query: () => api.eorSpeakingMonitoring.getEorSpeakingMonitoringStages(),
    }),
    nextEorSpeakingMonitoringStage: builder.query<
      EorMonitoringStage | null,
      { monitoringGroupId: number }
    >({
      query: ({ monitoringGroupId }) =>
        api.eorSpeakingMonitoring.getNextEorSpeakingMonitoringStage({
          monitoringGroupId: monitoringGroupId,
        }),
    }),
    createEorSpeakingMonitoring: builder.mutation<
      EorMonitoringDetails,
      { monitoring: EorMonitoringGroupDetails }
    >({
      query: ({ monitoring }) => api.eorSpeakingMonitoring.save(monitoring),
      invalidatesTags: ['EorSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    addEorSpeakingMonitoringStage: builder.mutation<
      EorMonitoringDetails,
      { stageDetails: EorMonitoringNewStageDetails }
    >({
      query: ({ stageDetails }) => api.eorSpeakingMonitoring.addStage(stageDetails),
      invalidatesTags: (_result, _error, { stageDetails }) => [
        { type: 'EorSpeakingMonitoring', id: stageDetails.monitoringGroupId },
        'EorSpeakingMonitoring',
        'ExaminerStatus',
        'Examiner',
      ],
    }),
    deleteEorSpeakingMonitoring: builder.mutation<void, { monitoringGroupId: number }>({
      query: ({ monitoringGroupId }) =>
        api.eorSpeakingMonitoring.delete({
          id: monitoringGroupId,
        }),
      invalidatesTags: (_result, _error, { monitoringGroupId }) => [
        { type: 'EorSpeakingMonitoring', id: monitoringGroupId },
        'EorSpeakingMonitoring',
        'ExaminerStatus',
        'Examiner',
      ],
    }),
    restoreEorSpeakingMonitoring: builder.mutation<void, { monitoringGroupId: number }>({
      query: ({ monitoringGroupId }) =>
        api.eorSpeakingMonitoring.restore({ id: monitoringGroupId }),
      invalidatesTags: (_result, _error, { monitoringGroupId }) => [
        { type: 'EorSpeakingMonitoring', id: monitoringGroupId },
        'EorSpeakingMonitoring',
        'ExaminerStatus',
        'Examiner',
      ],
    }),
    getPreSaveWarning: builder.mutation<EorSpeakingSuspension, EorMonitoringGroupDetails>({
      query: (monitoring) => api.eorSpeakingMonitoring.getPreSaveWarning(monitoring),
    }),
  }),
});

export const {
  useEorSpeakingMonitoringListQuery,
  useEorSpeakingMonitoringDetailsQuery,
  useEorSpeakingMonitoringReasonsQuery,
  useEorSpeakingMonitoringTypesQuery,
  useEorSpeakingMonitoringResultsQuery,
  useEorSpeakingMonitoringStagesQuery,
  useNextEorSpeakingMonitoringStageQuery,
  useCreateEorSpeakingMonitoringMutation,
  useAddEorSpeakingMonitoringStageMutation,
  useDeleteEorSpeakingMonitoringMutation,
  useRestoreEorSpeakingMonitoringMutation,
  useGetPreSaveWarningMutation,
} = eorSpeakingMonitoringApi;
